import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { useStatus, useData, combineStatuses } from 'resift';
import { useSelector } from 'react-redux';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import useGlobalConfig from 'helpers/useGlobalConfig';
import usePassiveRedux from '@sift/resift/usePassiveRedux';
import { Status, States } from '@sift/resift/models/Status';

// Component
import Attributes from './Attributes';
import Loader from 'components/Loader';

// Actions
import makeGetFieldsFetch from 'fetches/makeGetFieldsFetch';
import makeGetUserFieldsFetch from 'fetches/makeGetUserFieldsFetch';
import getProfileAttributes from './_helpers/getProfileAttributes';

// Styles
import withStyles from '@material-ui/core/styles/withStyles';

function Container({ classes, typeKey }) {
  const _client = useSelector(state => state.client);
  const globalConfig = useGlobalConfig();
  const entityTypes = useEntityTypes();
  const entityType = _get(entityTypes, typeKey);

  const makeGetUserFields = makeGetUserFieldsFetch();
  const userFieldsStatus = useStatus(makeGetUserFields);
  const userFields = useData(makeGetUserFields);

  const makeGetFields = makeGetFieldsFetch();
  const entityTypeFieldsStatus = useStatus(makeGetFields);
  const entityTypeFields = useData(makeGetFields);

  const subdomain = usePassiveRedux(state => state.client.subdomain);

  const filterChildAttributes = useCallback(() => {
    const fieldKeys = Object.keys(entityType._fields);
    const filteredFieldItemKeys = fieldKeys.filter(key =>
      _get(entityType, `_fields.${key}.item.type`, false),
    );
    const filteredFieldKeys = fieldKeys.filter(key => entityType._fields[key].fields);
    const childItemKeys = filteredFieldItemKeys.filter(
      key => entityType._fields[key].item.type.charAt(0) === '@',
    );
    const extractedChildItemKeys = childItemKeys.map(itemKey => {
      const itemLength = entityType._fields[itemKey].item.type.length;
      return entityType._fields[itemKey].item.type.substring(8, itemLength);
    });
    // (note - pearl) these two timezone fields should get filtered out but don't fit existing logic
    let omittableChildItemFields = ['timezoneAndWorkHours', 'timezoneAndWorkHours.workHoursItem'];
    extractedChildItemKeys.forEach(item => {
      omittableChildItemFields.push(item);
    });
    filteredFieldKeys.forEach(item => {
      entityType._fields[item].fields.forEach(field => {
        omittableChildItemFields.push(field);
      });
    });
    const filteredFieldList = fieldKeys.filter(field => {
      return !omittableChildItemFields.includes(field);
    });
    return filteredFieldList;
  }, [entityType]);

  const parentFields = useMemo(
    () =>
      typeKey === PERSON_ENTITY_TYPE_KEY
        ? filterChildAttributes().map(attribute => {
            return _get(userFields, 'results', []).find(field => field.objectKey === attribute);
          })
        : filterChildAttributes().map(attribute => {
            return _get(entityTypeFields, 'results', []).find(
              field => field.objectKey === attribute,
            );
          }),
    [typeKey, userFields, entityTypeFields],
  );

  const profileAttributes = useMemo(() => {
    return getProfileAttributes(typeKey, entityType, _client);
  }, [entityType, _client]);
  const parentAndProfileStatus =
    !profileAttributes || !parentFields ? new Status(States.LOADING) : new Status(States.NORMAL);

  const formats =
    typeKey === PERSON_ENTITY_TYPE_KEY
      ? _get(userFields, 'formats')
      : _get(entityTypeFields, 'formats');

  const configStatus =
    typeKey === PERSON_ENTITY_TYPE_KEY ? userFieldsStatus : entityTypeFieldsStatus;
  const combinedStatus = combineStatuses(parentAndProfileStatus.value, configStatus);

  return (
    <Loader status={combinedStatus} classes={{ root: classes.root }}>
      {() => (
        <Attributes
          formats={formats}
          globalConfig={globalConfig}
          subdomain={subdomain}
          parentFields={parentFields}
          profileAttributes={profileAttributes}
        />
      )}
    </Loader>
  );
}

Container.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = {
  root: {
    position: 'static',
  },
};

export default memo(withStyles(styles)(Container));
